/**
 * TabSelectionStep - Step 2b of SMB Vendor Cost Import (Excel files only)
 *
 * After vendor file upload, allows user to select which Excel tabs
 * to include in the import. Skipped for CSV files.
 */

import React, { useState } from 'react'
import { Space, Card, Alert, Button, Typography, List, Checkbox, Spin } from 'antd'
import {
  FileExcelOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { smbSyncService } from '../../../services/smbSyncService'
import { errorService } from '../../../services/errorService'
import type { StepProps } from './types'

const { Title, Text } = Typography

/**
 * Step 2b: Select which Excel tabs to include in vendor import
 */
export const TabSelectionStep: React.FC<StepProps> = ({ state, onNext, onBack, onCancel }) => {
  // Use previously selected tabs if available, otherwise default to all tabs
  const [selectedTabs, setSelectedTabs] = useState<string[]>(
    state.selectedTabs.length > 0 ? state.selectedTabs : state.vendorTabs
  )
  const [isProcessing, setIsProcessing] = useState(false)

  const handleSelectAll = () => {
    setSelectedTabs([...state.vendorTabs])
  }

  const handleSelectNone = () => {
    setSelectedTabs([])
  }

  const handleTabToggle = (tabName: string) => {
    setSelectedTabs(prev => {
      if (prev.includes(tabName)) {
        return prev.filter(t => t !== tabName)
      } else {
        return [...prev, tabName]
      }
    })
  }

  const handleNext = async () => {
    if (selectedTabs.length === 0) {
      errorService.showError(
        'No Tabs Selected',
        'Please select at least one tab to include in the import',
        [{ code: 'NO_TABS_SELECTED', message: 'At least one tab must be selected' }]
      )
      return
    }

    setIsProcessing(true)

    try {
      // Call backend to process selected tabs and get columns
      const response = await smbSyncService.selectVendorTabs(state.jobId, selectedTabs)

      if (!response.success || !response.data) {
        const errorMsg = response.message || 'Failed to process selected tabs'
        errorService.showError('Tab Processing Failed', errorMsg, response.errors)
        setIsProcessing(false)
        return
      }

      // Success - advance to field mapping (sample data will be fetched in FieldMappingStep)
      onNext({
        selectedTabs,
        currentStep: 'mapping',
      })
    } catch (err: any) {
      errorService.showError('Processing Error', err.message || 'Failed to process tabs')
      setIsProcessing(false)
    }
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Instructions */}
      <Card>
        <Title level={4}>Step 2b: Select Tabs to Include</Title>
        <Text>
          Your vendor file contains multiple tabs. Select which tabs you want to include
          in this import. Data from selected tabs will be combined for field mapping.
        </Text>
      </Card>

      {/* Tab Selection */}
      <Card>
        {isProcessing ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: '32px' }} />} />
            <div style={{ marginTop: '16px' }}>
              <Text>Processing selected tabs...</Text>
            </div>
          </div>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {/* Selection controls */}
            <Space style={{ marginBottom: '16px' }}>
              <Button size="small" onClick={handleSelectAll}>Select All</Button>
              <Button size="small" onClick={handleSelectNone}>Select None</Button>
              <Text strong>Available Tabs ({state.vendorTabs.length}):</Text>
            </Space>

            {/* Tab list with checkboxes */}
            <List
              bordered
              dataSource={state.vendorTabs}
              style={{ maxHeight: '300px', overflow: 'auto' }}
              renderItem={(tabName: string) => (
                <List.Item>
                  <Checkbox
                    checked={selectedTabs.includes(tabName)}
                    onChange={() => handleTabToggle(tabName)}
                  >
                    <Space>
                      <FileExcelOutlined />
                      <Text>{tabName}</Text>
                    </Space>
                  </Checkbox>
                </List.Item>
              )}
            />

            {/* Selection summary */}
            <Alert
              type={selectedTabs.length > 0 ? 'info' : 'warning'}
              message={`${selectedTabs.length} of ${state.vendorTabs.length} tabs selected`}
              showIcon
              style={{ marginTop: '16px' }}
            />
          </Space>
        )}
      </Card>

      {/* Action buttons */}
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button
              size="large"
              onClick={onBack}
              disabled={isProcessing}
            >
              Back
            </Button>
            <Button
              size="large"
              onClick={onCancel}
              disabled={isProcessing}
            >
              Cancel
            </Button>
          </Space>
          <Button
            type="primary"
            size="large"
            onClick={handleNext}
            disabled={selectedTabs.length === 0 || isProcessing}
          >
            Next: Map Fields
          </Button>
        </Space>
      </Card>
    </Space>
  )
}
